if (!customElements.get('bike-sizing-tool')) {
  class BikeSizingTool extends HTMLElement {
    constructor() {
      super();

      // Constants
      this.STORAGE_KEY = 'bikeSizingData';
      this.HIGHLIGHT_STATE_KEY = 'bikeSizingHighlightActive';
      this.ADJUSTMENT_FACTOR = 0.5;

      // State variables
      this.userHeight = 0;
      this.userInseam = 0;
      this.calculatedAdjustedHeight = 0;
      this.isNonProductContext = this.dataset.useProducts === 'no-product';

      this.customerId = this.getAttribute('data-customer-id');
      this.customerHash = this.getAttribute('data-customer-hash');
      this.height = this.getAttribute('data-height');
      this.inseam = this.getAttribute('data-inseam');

      // Reused HTML
      this.measurementsHtml = `<a href="#" id="changeMeasurementsLink" class="twp-sizing-tool-initial-subtext__link">Change my measurements</a>`;

      // Selectors object
      this.selectors = {
        sizingButton: '.twp-sizing-tool__button',
        overlay: '#sizingToolOverlay',
        closeButton: '#closeButton',
        slide1: '#slide1',
        slide2: '#slide2',
        slide3: '#slide3',
        slide4: '#slide4',
        nextToInseamBtn: '#nextToInseam',
        calculateResultsBtn: '#calculateResults',
        heightInput: '#heightInput',
        inseamInput: '#inseamInput',
        heightError: '#heightError',
        inseamError: '#inseamError',
        resultHeight: '#resultHeight',
        resultInseam: '#resultInseam',
        resultAdjustedHeight: '#resultAdjustedHeight',
        restartButton: '#restartButton',
        backToHeightBtn: '#backToHeight',
        visualScaleItems: '.twp-sizing-tool__visual-scale__item',
        resultsContent: '.twp-sizing-tool__results-content',
        fallbackMessage: '.twp-sizing-tool__fallback-message',
        continueButton: '#continueButton',
        sizingSummary: '#sizingSummary',
        detailsButton: '#detailsButton',
        initialSizingButton: '#initialSizingButton',
        plpContinueButton: '#plpContinueButton',
        initialContainer: '.twp-sizing-tool__container',
        initialHeading: '#twp-sizing-tool-initial-heading',
        initialSubtext: '#twp-sizing-tool-initial-subtext',
        changeMeasurementsLink: '#changeMeasurementsLink',
        switchSlider: '.switch-slider',
        markerElement: '.twp-sizing-tool__marker-element',
        collectionContainer: '.template-collection',
        searchContainer: '.template-search',
        heightResult: '[data-height-result]',
        inseamResult: '[data-inseam-result]',
      };

      // Bind methods to this
      this.openPopup = this.openPopup.bind(this);
      this.closePopup = this.closePopup.bind(this);
      this.showSlide = this.showSlide.bind(this);
      this.goToInseamSlide = this.goToInseamSlide.bind(this);
      this.showResults = this.showResults.bind(this);
      this.restartProcess = this.restartProcess.bind(this);
      this.completeSizing = this.completeSizing.bind(this);
      this.handlePlpContinue = this.handlePlpContinue.bind(this);
      this.openPopupToResults = this.openPopupToResults.bind(this);
      this.handleContainerClick = this.handleContainerClick.bind(this);
      this.handleContainerKeydown = this.handleContainerKeydown.bind(this);
      this.refreshProductCards = this.refreshProductCards.bind(this);
      this.sendMeasurementsToServer = this.sendMeasurementsToServer.bind(this);
      this.handleRecommendedSizeUpdate = this.handleRecommendedSizeUpdate.bind(this);
    }

    connectedCallback() {
      this.loadFromLocalStorage();

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.setupInitialView();
          this.setupEventListeners();
        });
      } else {
        this.setupInitialView();
        this.setupEventListeners();
      }
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    setupEventListeners() {
      const sizingButton = this.querySelector(this.selectors.sizingButton) ? this.querySelector(this.selectors.sizingButton) : document.querySelector(this.selectors.sizingButton);
      const closeButton = document.querySelector(this.selectors.closeButton);
      const overlay = document.querySelector(this.selectors.overlay);
      const nextToInseamBtn = document.querySelector(this.selectors.nextToInseamBtn);
      const calculateResultsBtn = document.querySelector(this.selectors.calculateResultsBtn);
      const restartButton = document.querySelector(this.selectors.restartButton);
      const backToHeightBtn = document.querySelector(this.selectors.backToHeightBtn);
      const continueButton = document.querySelector(this.selectors.continueButton);
      const detailsButton = document.querySelector(this.selectors.detailsButton);
      const initialSizingButton = this.querySelector(this.selectors.initialSizingButton);
      const plpContinueButton = document.querySelector(this.selectors.plpContinueButton);
      const initialContainer = this.querySelector(this.selectors.initialContainer);

      if (sizingButton) sizingButton.addEventListener('click', this.openPopup);
      if (closeButton) closeButton.addEventListener('click', this.closePopup);
      if (overlay)
        overlay.addEventListener('click', e => {
          if (e.target === overlay) this.closePopup();
        });
      if (nextToInseamBtn) nextToInseamBtn.addEventListener('click', this.goToInseamSlide);
      if (calculateResultsBtn) calculateResultsBtn.addEventListener('click', this.showResults);
      if (restartButton) restartButton.addEventListener('click', this.restartProcess);
      if (backToHeightBtn)
        backToHeightBtn.addEventListener('click', () =>
          this.showSlide(document.querySelector(this.selectors.slide1))
        );
      if (continueButton) continueButton.addEventListener('click', this.completeSizing);
      if (detailsButton) detailsButton.addEventListener('click', this.openPopupToResults);
      if (initialSizingButton) {
        initialSizingButton.addEventListener('click', () => {
          if (initialSizingButton.textContent === 'Details') {
            this.openPopupToResults();
          } else {
            this.openPopup();
          }
        });
      }
      if (plpContinueButton) plpContinueButton.addEventListener('click', this.handlePlpContinue);
      if (initialContainer) {
        initialContainer.addEventListener('click', this.handleContainerClick);
        initialContainer.addEventListener('keydown', this.handleContainerKeydown);
      }

      const heightInput = document.querySelector(this.selectors.heightInput);
      const inseamInput = document.querySelector(this.selectors.inseamInput);

      if (heightInput) {
        heightInput.addEventListener('input', () => {
          this.clearError(this.selectors.heightError);
        });
      }

      if (inseamInput) {
        inseamInput.addEventListener('input', () => {
          this.clearError(this.selectors.inseamError);
        });
      }

      window.addEventListener('recommended-size-updated', this.handleRecommendedSizeUpdate);
    }

    removeEventListeners() {
      const sizingButton = this.querySelector(this.selectors.sizingButton) ? this.querySelector(this.selectors.sizingButton) : document.querySelector(this.selectors.sizingButton);
      const closeButton = document.querySelector(this.selectors.closeButton);
      const overlay = document.querySelector(this.selectors.overlay);
      const nextToInseamBtn = document.querySelector(this.selectors.nextToInseamBtn);
      const calculateResultsBtn = document.querySelector(this.selectors.calculateResultsBtn);
      const restartButton = document.querySelector(this.selectors.restartButton);
      const backToHeightBtn = document.querySelector(this.selectors.backToHeightBtn);
      const continueButton = document.querySelector(this.selectors.continueButton);
      const detailsButton = document.querySelector(this.selectors.detailsButton);
      const initialSizingButton = document.querySelector(this.selectors.initialSizingButton);
      const plpContinueButton = document.querySelector(this.selectors.plpContinueButton);
      const initialContainer = document.querySelector(this.selectors.initialContainer);

      if (sizingButton) sizingButton.removeEventListener('click', this.openPopup);
      if (closeButton) closeButton.removeEventListener('click', this.closePopup);
      if (nextToInseamBtn) nextToInseamBtn.removeEventListener('click', this.goToInseamSlide);
      if (calculateResultsBtn) calculateResultsBtn.removeEventListener('click', this.showResults);
      if (restartButton) restartButton.removeEventListener('click', this.restartProcess);
      if (continueButton) continueButton.removeEventListener('click', this.completeSizing);
      if (detailsButton) detailsButton.removeEventListener('click', this.openPopupToResults);
      if (plpContinueButton) plpContinueButton.removeEventListener('click', this.handlePlpContinue);
      if (initialContainer) {
        initialContainer.removeEventListener('click', this.handleContainerClick);
        initialContainer.removeEventListener('keydown', this.handleContainerKeydown);
      }

      const heightInput = document.querySelector(this.selectors.heightInput);
      const inseamInput = document.querySelector(this.selectors.inseamInput);

      if (heightInput) {
        heightInput.removeEventListener('input', () => {
          this.clearError(this.selectors.heightError);
        });
      }

      if (inseamInput) {
        inseamInput.removeEventListener('input', () => {
          this.clearError(this.selectors.inseamError);
        });
      }

      window.removeEventListener('recommended-size-updated', this.handleRecommendedSizeUpdate);
    }

    handleRecommendedSizeUpdate(event) {
      // Only update if the event was triggered from a different instance
      if (event.detail?.source !== this) {
        this.setupInitialView();
      }
    }

    handleContainerClick(e) {
      // Handle "Change my measurements" link
      if (e.target.matches('#changeMeasurementsLink')) {
        e.preventDefault();
        this.openPopup();
        return;
      }

      // Handle Switch Slider toggle
      if (e.target.matches('.switch-slider')) {
        const slider = e.target;
        let currentState = localStorage.getItem(this.HIGHLIGHT_STATE_KEY) === 'true';
        const newState = !currentState;

        localStorage.setItem(this.HIGHLIGHT_STATE_KEY, newState.toString());
        slider.classList.toggle('is-active', newState);
        slider.setAttribute('aria-checked', newState.toString());

        this.refreshProductCards();
      }
    }

    handleContainerKeydown(e) {
      if (e.target.matches('.switch-slider') && (e.key === ' ' || e.key === 'Enter')) {
        e.preventDefault();
        e.target.click();
      }
    }

    openPopup() {
      const overlay = document.querySelector(this.selectors.overlay);
      const slide1 = document.querySelector(this.selectors.slide1);
      if (overlay) overlay.classList.add('active');
      if (slide1) {
        this.loadFromLocalStorage();
        this.showSlide(slide1);
      } else {
        console.error('Sizing tool popup structure not found (slide1).');
      }
    }

    closePopup() {
      const overlay = document.querySelector(this.selectors.overlay);
      if (overlay) overlay.classList.remove('active');
    }

    showSlide(slide) {
      const slide1 = document.querySelector(this.selectors.slide1);
      const slide2 = document.querySelector(this.selectors.slide2);
      const slide3 = document.querySelector(this.selectors.slide3);
      const slide4 = document.querySelector(this.selectors.slide4);

      [slide1, slide2, slide3, slide4].forEach(s => {
        if (s) s.classList.remove('active');
      });

      if (slide) slide.classList.add('active');
    }

    goToInseamSlide() {
      const heightInput = document.querySelector(this.selectors.heightInput);
      const inseamInput = document.querySelector(this.selectors.inseamInput);
      const slide2 = document.querySelector(this.selectors.slide2);

      if (!heightInput.value) {
        this.showError(this.selectors.heightError, 'Please enter your height');
        return;
      }

      const height = parseFloat(heightInput.value);
      this.userHeight = height;
      this.saveToLocalStorage('height', height);

      const averageInseam = (height * 0.47).toFixed(1);
      inseamInput.placeholder = averageInseam;

      if (!inseamInput.value) {
        inseamInput.value = averageInseam;
      }

      this.showSlide(slide2);
    }

    showResults() {
      const heightInput = document.querySelector(this.selectors.heightInput);
      const inseamInput = document.querySelector(this.selectors.inseamInput);
      const resultHeight = document.querySelector(this.selectors.resultHeight);
      const resultInseam = document.querySelector(this.selectors.resultInseam);
      const resultAdjustedHeight = document.querySelector(this.selectors.resultAdjustedHeight);
      const overlay = document.querySelector(this.selectors.overlay);
      const slide3 = document.querySelector(this.selectors.slide3);
      const slide4 = document.querySelector(this.selectors.slide4);

      if (!inseamInput.value) {
        this.showError(this.selectors.inseamError, 'Please enter your inseam');
        return;
      }

      const height = parseFloat(heightInput.value);
      const inseam = parseFloat(inseamInput.value);
      this.userInseam = inseam;

      this.saveToLocalStorage('inseam', inseam);

      // Send measurements to server
      this.sendMeasurementsToServer();

      const adjustedHeight = this.calculateAdjustedHeight(height, inseam);
      this.calculatedAdjustedHeight = adjustedHeight;
      this.saveToLocalStorage('adjustedHeight', adjustedHeight);

      const recommendation = this.determineRecommendation(adjustedHeight);

      this.saveToLocalStorage(
        'variantTitle',
        recommendation.isFallback ? undefined : recommendation.variantTitle
      );
      localStorage.setItem('bikeSizingToolFallback', recommendation.isFallback.toString());
      localStorage.setItem(this.HIGHLIGHT_STATE_KEY, 'true');
      window.dispatchEvent(new CustomEvent('recommended-size-updated', {
        detail: { source: this }
      }));

      const isPlpContext =
        (overlay && overlay.classList.contains('context--plp')) ||
        overlay.classList.contains('context--search');

      const subTextContent = this.querySelector(this.selectors.subTextContent);
      if (subTextContent && this.userHeight && this.userInseam) {
        subTextContent.innerHTML = `Your measurements: ${this.userHeight}cm / ${this.userInseam}cm ${this.measurementsHtml}`;
      }

      if (this.isNonProductContext) {
        this.setupInitialView();
        this.closePopup();
        return;
      } else if (isPlpContext) {
        this.showSlide(slide4);
      } else {
        resultHeight.textContent = `${height.toFixed(1)} cm`;
        resultInseam.textContent = `${inseam.toFixed(1)} cm`;
        resultAdjustedHeight.textContent = `${adjustedHeight.toFixed(1)} cm`;

        this.updateVisualScale(adjustedHeight, recommendation);
        this.showSlide(slide3);
      }
    }

    calculateAdjustedHeight(height, inseam) {
      return height + (inseam - height * 0.47) * this.ADJUSTMENT_FACTOR;
    }

    saveToLocalStorage(key, value) {
      try {
        let data = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}');
        data[key] = value;
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        // Silent error
      }
    }

    loadFromLocalStorage(key) {
      try {
        const data = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}');

        // Prioritize attribute-based data if available
        if (key === 'height' && this.height) {
          return parseFloat(this.height);
        } else if (key === 'inseam' && this.inseam) {
          return parseFloat(this.inseam);
        }

        return key ? data[key] : data;
      } catch (e) {
        return key ? undefined : {};
      }
    }

    restartProcess() {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem('bikeSizingToolFallback');
      this.dataset.height = '';
      this.height = '';
      this.dataset.inseam = '';
      this.inseam = '';

      const heightInput = document.querySelector(this.selectors.heightInput);
      const inseamInput = document.querySelector(this.selectors.inseamInput);
      const resultHeight = document.querySelector(this.selectors.resultHeight);
      const resultInseam = document.querySelector(this.selectors.resultInseam);
      const resultAdjustedHeight = document.querySelector(this.selectors.resultAdjustedHeight);
      const fallbackMessage = document.querySelector(this.selectors.fallbackMessage);
      const resultsContent = document.querySelector(this.selectors.resultsContent);
      const slide1 = document.querySelector(this.selectors.slide1);

      if (heightInput) heightInput.value = '';
      if (inseamInput) {
        inseamInput.value = '';
        inseamInput.placeholder = '87.6';
      }

      this.userHeight = 0;
      this.userInseam = 0;
      this.calculatedAdjustedHeight = 0;

      if (resultHeight) resultHeight.textContent = '--';
      if (resultInseam) resultInseam.textContent = '--';
      if (resultAdjustedHeight) resultAdjustedHeight.textContent = '--';

      this.resetVisualScale();

      if (fallbackMessage) fallbackMessage.style.display = 'none';
      if (resultsContent) resultsContent.removeAttribute('style');

      this.setupInitialView();
      this.showSlide(slide1);
    }

    updateVisualScale(adjustedHeight, recommendationResult) {
      const resultsContent = document.querySelector(this.selectors.resultsContent);
      const fallbackMessage = document.querySelector(this.selectors.fallbackMessage);
      const continueButton = document.querySelector(this.selectors.continueButton);
      const visualScaleItems = document.querySelectorAll(this.selectors.visualScaleItems);

      this.resetVisualScale();

      const isFallback = recommendationResult.isFallback;

      if (isFallback) {
        if (resultsContent) resultsContent.style.display = 'none';
        if (fallbackMessage) fallbackMessage.removeAttribute('style');
        visualScaleItems.forEach(item => item.classList.add('hidden-variant'));
        if (continueButton) continueButton.style.display = 'none';
      } else {
        if (resultsContent) resultsContent.removeAttribute('style');
        if (fallbackMessage) fallbackMessage.style.display = 'none';

        let bestMatchItem = null;
        let bestMatchPercentage = 50;
        let targetIndex = -1;

        visualScaleItems.forEach((item, index) => {
          let currentTitle = item.textContent.trim();
          currentTitle = currentTitle.replace(/\s+/g, ' ');
          const markerText = item.querySelector(this.selectors.markerElement)?.textContent || '';
          if (markerText) {
            currentTitle = currentTitle.replace(markerText, '').trim();
          }
          currentTitle = currentTitle.replace('Your size', '').trim();

          if (currentTitle === recommendationResult.variantTitle) {
            bestMatchItem = item;
            targetIndex = index;

            const minHeight = parseFloat(item.dataset.minHeight);
            const maxHeight = parseFloat(item.dataset.maxHeight);
            if (!isNaN(minHeight) && !isNaN(maxHeight)) {
              const range = maxHeight - minHeight;
              const positionInRange = adjustedHeight - minHeight;
              bestMatchPercentage = range > 0 ? (positionInRange / range) * 100 : 50;
              bestMatchPercentage = Math.max(0, Math.min(100, bestMatchPercentage));
            }
            return;
          }
        });

        if (bestMatchItem) {
          bestMatchItem.classList.add('highlight');
          const markerSpan = bestMatchItem.querySelector(this.selectors.markerElement);
          if (markerSpan) {
            markerSpan.style.setProperty('--marker-position', `${bestMatchPercentage.toFixed(1)}%`);
            markerSpan.style.opacity = '1';
          }

          const numVariants = visualScaleItems.length;
          let displayIndices = [];

          if (numVariants <= 2) {
            displayIndices = Array.from(visualScaleItems.keys());
          } else if (targetIndex === 0) {
            displayIndices = [0, 1];
          } else if (targetIndex === numVariants - 1) {
            displayIndices = [numVariants - 2, numVariants - 1];
          } else {
            displayIndices = [targetIndex - 1, targetIndex, targetIndex + 1];
          }

          visualScaleItems.forEach((item, index) => {
            item.classList.toggle('hidden-variant', !displayIndices.includes(index));
          });
        } else {
          visualScaleItems.forEach(item => item.classList.add('hidden-variant'));
        }

        if (continueButton) continueButton.style.display = 'inline-block';
      }
    }

    resetVisualScale() {
      const visualScaleItems = document.querySelectorAll(this.selectors.visualScaleItems);
      const resultHeight = document.querySelector(this.selectors.resultHeight);
      const resultInseam = document.querySelector(this.selectors.resultInseam);
      const resultAdjustedHeight = document.querySelector(this.selectors.resultAdjustedHeight);

      visualScaleItems.forEach(item => {
        item.classList.remove('highlight');
        item.classList.remove('hidden-variant');
        const markerSpan = item.querySelector(this.selectors.markerElement);
        if (markerSpan) {
          markerSpan.style.removeProperty('--marker-position');
          markerSpan.style.opacity = '0';
        }
      });

      if (resultHeight) resultHeight.textContent = '--';
      if (resultInseam) resultInseam.textContent = '--';
      if (resultAdjustedHeight) resultAdjustedHeight.textContent = '--';
    }

    completeSizing() {
      this.closePopup();
      this.setupInitialView();
    }

    handlePlpContinue() {
      this.closePopup();
      this.setupInitialView();
    }

    openPopupToResults() {
      const overlay = document.querySelector(this.selectors.overlay);
      const heightInput = document.querySelector(this.selectors.heightInput);
      const inseamInput = document.querySelector(this.selectors.inseamInput);

      if (overlay) overlay.classList.add('active');

      const savedHeight = this.height
        ? parseFloat(this.height)
        : this.loadFromLocalStorage('height');
      const savedInseam = this.inseam
        ? parseFloat(this.inseam)
        : this.loadFromLocalStorage('inseam');

      if (heightInput && savedHeight !== undefined) {
        heightInput.value = parseFloat(savedHeight).toFixed(1);
      }
      if (inseamInput && savedInseam !== undefined) {
        inseamInput.value = parseFloat(savedInseam).toFixed(1);
      }

      this.userHeight = savedHeight ? parseFloat(savedHeight) : 0;
      this.userInseam = savedInseam ? parseFloat(savedInseam) : 0;

      this.showResults();
    }

    determineRecommendation(adjustedHeight) {
      const visualScaleItems = document.querySelectorAll(this.selectors.visualScaleItems);
      let potentialMatches = [];
      let foundVariant = false;
      let bestMatch = null;

      visualScaleItems.forEach(item => {
        const minHeightStr = item.dataset.minHeight;
        const maxHeightStr = item.dataset.maxHeight;
        const minHeight = parseFloat(minHeightStr);
        const maxHeight = parseFloat(maxHeightStr);

        if (isNaN(minHeight) || isNaN(maxHeight) || minHeightStr === '' || maxHeightStr === '') {
          return;
        }

        if (
          !isNaN(minHeight) &&
          !isNaN(maxHeight) &&
          adjustedHeight >= minHeight &&
          adjustedHeight <= maxHeight
        ) {
          foundVariant = true;
          const range = maxHeight - minHeight;
          const positionInRange = adjustedHeight - minHeight;
          let percentageInItem = range > 0 ? (positionInRange / range) * 100 : 50;
          percentageInItem = Math.max(0, Math.min(100, percentageInItem));

          potentialMatches.push({
            item: item,
            percentage: percentageInItem,
            centrality: Math.abs(percentageInItem - 50),
          });
        }
      });

      const isFallback = !foundVariant;

      if (!isFallback) {
        if (potentialMatches.length === 1) {
          bestMatch = potentialMatches[0];
        } else if (potentialMatches.length > 1) {
          potentialMatches.sort((a, b) => a.centrality - b.centrality);
          bestMatch = potentialMatches[0];
        }
      }

      let variantTitle = null;
      if (bestMatch && bestMatch.item) {
        let rawTitle = bestMatch.item.textContent.trim();
        rawTitle = rawTitle.replace(/\s+/g, ' ');
        const markerText =
          bestMatch.item.querySelector(this.selectors.markerElement)?.textContent || '';
        if (markerText) {
          rawTitle = rawTitle.replace(markerText, '').trim();
        }
        rawTitle = rawTitle.replace('Your size', '').trim();
        variantTitle = rawTitle;
      }

      return {
        variantTitle: variantTitle,
        isFallback: isFallback,
      };
    }

    setupInitialView() {
      const initialContainer = this.querySelector(this.selectors.initialContainer);
      const initialHeading = this.querySelector(this.selectors.initialHeading);
      const initialSubtext = this.querySelector(this.selectors.initialSubtext);
      const initialSizingButton = this.querySelector(this.selectors.initialSizingButton);
      const sizingSummary = this.querySelector(this.selectors.sizingSummary);

      if (!initialContainer || !initialHeading || !initialSubtext || !initialSizingButton) {
        return;
      }

      const existingSliderAtStart = initialContainer.querySelector(this.selectors.switchSlider);
      if (existingSliderAtStart) {
        existingSliderAtStart.remove();
      }

      const savedData = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}');

      // Only use attribute data if there are no localStorage values
      if (!savedData.height && this.height) savedData.height = this.height;
      if (!savedData.inseam && this.inseam) savedData.inseam = this.inseam;

      const hasSavedMeasurements =
        savedData &&
        typeof savedData === 'object' &&
        Object.keys(savedData).length > 0 &&
        savedData.height &&
        savedData.inseam;
      const isFallback = localStorage.getItem('bikeSizingToolFallback') === 'true';
      const savedVariantTitle = this.loadFromLocalStorage('variantTitle');

      const isPlpContext =
        document.querySelector(this.selectors.collectionContainer) !== null ||
        document.querySelector(this.selectors.searchContainer) !== null;

      if (sizingSummary) sizingSummary.style.display = 'none';
      initialContainer.style.display = 'flex';

      initialSizingButton.style.display = '';

      let headingText = 'Not sure what size is best?';
      let subTextContent =
        "Answer a few short questions and we'll make a recommendation on which size is best for you.";
      let buttonText = 'Find Your Size';
      let useHtmlForSubtext = false;

      const savedHeight = hasSavedMeasurements ? parseFloat(savedData.height).toFixed(1) : null;
      const savedInseam = hasSavedMeasurements ? parseFloat(savedData.inseam).toFixed(1) : null;
      const measurementText =
        savedHeight && savedInseam
          ? `Your measurements: ${savedHeight} cm / ${savedInseam} cm`
          : 'No measurements saved.';

      if (this.isNonProductContext) {
        const heightResult = this.querySelector(this.selectors.initialHeading);
        const inseamResult = this.querySelector(this.selectors.initialSubtext);

        if (!heightResult || !inseamResult) return;

        const height = heightResult.dataset?.heightResult || savedHeight;
        const inseam = inseamResult.dataset?.inseamResult || savedInseam;

        if (height && inseam) {
          heightResult.parentElement.classList.add('twp-sizing-tool__measurements');
          heightResult.innerHTML = `<b>Height:</b> ${height}cm`;
          inseamResult.innerHTML = `<b>Inseam:</b> ${inseam}cm`;
        }
      } else if (isPlpContext && hasSavedMeasurements) {
        headingText = 'Pre-select your size';
        subTextContent = `${measurementText} ${this.measurementsHtml}`;
        useHtmlForSubtext = true;
        initialSizingButton.style.display = 'none';

        if (localStorage.getItem(this.HIGHLIGHT_STATE_KEY) === null) {
          localStorage.setItem(this.HIGHLIGHT_STATE_KEY, 'true');
        }
        const isHighlightActive = localStorage.getItem(this.HIGHLIGHT_STATE_KEY) === 'true';

        const switchSlider = document.createElement('span');
        switchSlider.className = 'switch-slider';
        if (isHighlightActive) {
          switchSlider.classList.add('is-active');
        }
        switchSlider.setAttribute('role', 'switch');
        switchSlider.setAttribute('aria-checked', isHighlightActive.toString());
        switchSlider.setAttribute('tabindex', '0');
        switchSlider.setAttribute('aria-label', 'Toggle size highlighting');

        initialContainer.appendChild(switchSlider);

        initialHeading.textContent = headingText;
        initialSubtext.innerHTML = subTextContent;
      } else {
        initialSizingButton.style.display = '';
        const existingSlider = initialContainer.querySelector(this.selectors.switchSlider);
        if (existingSlider) {
          existingSlider.remove();
        }

        if (isFallback) {
          headingText = "We can't find a size to suit you";
          subTextContent = measurementText;
          buttonText = 'Change details';
        } else if (
          savedVariantTitle &&
          savedVariantTitle !== 'N/A' &&
          savedVariantTitle !== undefined
        ) {
          headingText = `For you we recommend: ${savedVariantTitle}`;
          subTextContent = measurementText;
          buttonText = 'Details';
        } else if (hasSavedMeasurements) {
          headingText = 'Update your recommendation?';
          subTextContent = measurementText;
          buttonText = 'Change details';
        }

        initialHeading.textContent = headingText;
        initialSubtext.textContent = subTextContent;
        initialSizingButton.textContent = buttonText;
      }
    }

    refreshProductCards() {
      if (window.FoxTheme?.PLPFormUtils?.initializeAllProductCards) {
        window.FoxTheme.PLPFormUtils.initializeAllProductCards();
      }
    }

    showError(selector, message) {
      const errorElement = document.querySelector(selector);
      if (errorElement) {
        errorElement.textContent = message;
        errorElement.classList.add('active');
      }
    }

    clearError(selector) {
      const errorElement = document.querySelector(selector);
      if (errorElement) {
        errorElement.textContent = '';
        errorElement.classList.remove('active');
      }
    }

    // Add new method to send measurements to server
    sendMeasurementsToServer() {
      const hasHeight = this.userHeight || this.userHeight == 0;
      const hasInseam = this.userInseam || this.userInseam == 0;

      if (!hasHeight || !hasInseam || !this.customerId) return;

      fetch(`${window.theme.middlewareAPI.url}/account/sizingupdate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({
          customer_id: this.customerId,
          customer_id_hash: this.customerHash,
          height: this.userHeight,
          inseam: this.userInseam,
        }),
      }).catch(error => {
        console.error('Error sending measurements:', error);
        return;
      });
    }
  }

  // Register the custom element
  customElements.define('bike-sizing-tool', BikeSizingTool);
}
